import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '../config';
import { BackendResponse } from '../../types/interfaces';
import { deserializeResponse, replaceUrlParams } from '../utils/helpers';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import {
  DetailedProduct,
  GetProductsResponse,
  LastOrderedProducts,
  Product,
} from '../../../shared/models/product.model';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../utils/snackbar.service';
import { TestCustomerService } from '../test-customer/test-customer.service';
import { ResponseStatus } from '../../types/enums';

interface GetProductsParams {
  search?: string;
  newProducts?: boolean;
  groupByCategory?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private testCustomerService: TestCustomerService
  ) {}

  getProducts(
    params?: GetProductsParams
  ): Observable<BackendResponse<GetProductsResponse>> {
    const url = this.configService.endpoints.products.getProducts;
    let httpParams = new HttpParams();
    if (params.search) {
      httpParams = httpParams.set('search', params.search);
    }
    if (params.newProducts !== undefined) {
      httpParams = httpParams.set('newProducts', String(params.newProducts));
    }
    if (params.groupByCategory !== undefined) {
      httpParams = httpParams.set(
        'groupByCategory',
        String(params.groupByCategory)
      );
    }
    return this.http
      .get<BackendResponse<GetProductsResponse>>(url, { params: httpParams })
      .pipe(
        map((arg: any) =>
          deserializeResponse<GetProductsResponse>(arg, GetProductsResponse)
        ),
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant('products.error.inProductsRetrieving')
          );
          return throwError(() => error);
        })
      );
  }

  getCategoryProducts(
    categoryId: number
  ): Observable<BackendResponse<Product[]>> {
    const url = replaceUrlParams(
      this.configService.endpoints.products.getCategoryProducts,
      ['categoryId'],
      [categoryId]
    );
    return this.http
      .get<BackendResponse<Product[]>>(url, {
        params: new HttpParams().set('publish', '1'),
      })
      .pipe(
        map((arg: any) => deserializeResponse<Product[]>(arg, Product)),
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant('products.error.inProductsRetrieving')
          );
          return throwError(() => error);
        })
      );
  }

  getProductsByIds(
    productIds: number[]
  ): Observable<BackendResponse<Product[]>> {
    const url = this.configService.endpoints.products.getProductsByIds;

    return this.http
      .post<BackendResponse<Product[]>>(url, { ids: productIds })
      .pipe(map((arg: any) => deserializeResponse<Product[]>(arg, Product)));
  }

  getProductsInCart(
    ids: number[] = []
  ): Observable<BackendResponse<Product[]>> {
    const url = this.configService.endpoints.products.productsInCart;
    return this.http
      .post<BackendResponse<Product[]>>(`${url}`, { ids })
      .pipe(map((arg: any) => deserializeResponse<Product[]>(arg, Product)));
  }

  getFavouriteProducts(
    residentId: number
  ): Observable<BackendResponse<Product[]>> {
    const url = replaceUrlParams(
      this.configService.endpoints.products.favouriteProducts,
      ['residentId'],
      [residentId]
    );
    return this.http.get<BackendResponse<Product[]>>(url).pipe(
      map((arg: any) => deserializeResponse<Product[]>(arg, Product)),
      map(response => {
        if (!this.testCustomerService.isLoggedInAsTestCustomer()) {
          return response;
        }

        const testCustomerProducts =
          this.testCustomerService.getFavouriteProducts(residentId);
        const data = [...response.data];

        testCustomerProducts.forEach(product => {
          if (
            !data.some(existingProduct => existingProduct.id === product.id)
          ) {
            data.push(product);
          }
        });
        return {
          ...response,
          data,
          message:
            response.message ||
            'Favourite products loaded from API and test customer storage',
        };
      }),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant(
            'products.error.inFavourtieProductsRetrieving'
          )
        );
        return throwError(() => error);
      })
    );
  }

  getLastOrderedProducts(
    residentId: number
  ): Observable<BackendResponse<LastOrderedProducts>> {
    const url = replaceUrlParams(
      this.configService.endpoints.orders.getLastOrderedProducts,
      ['residentId'],
      [residentId]
    );
    return this.http.get<BackendResponse<LastOrderedProducts>>(url).pipe(
      map((arg: any) =>
        deserializeResponse<LastOrderedProducts>(arg, LastOrderedProducts)
      ),
      catchError(error => {
        if (error.isHandled) return;
        this.snackbarService.error(
          this.translateService.instant(
            'products.error.inLastOrderedProductsRetrieving'
          )
        );
        return throwError(() => error);
      })
    );
  }

  getProductDetails(
    productId: number,
    residentIds?: number[]
  ): Observable<BackendResponse<DetailedProduct>> {
    const url = replaceUrlParams(
      this.configService.endpoints.products.productDetails,
      ['productId'],
      [productId]
    );
    return this.http
      .post<DetailedProduct>(url, { residentIds })
      .pipe(
        map((arg: any) =>
          deserializeResponse<DetailedProduct>(arg, DetailedProduct)
        )
      );
  }

  getSubcategoryProducts(
    subcategory: string
  ): Observable<BackendResponse<Product[]>> {
    const url = this.configService.endpoints.products.getSubcategoryProducts;
    let httpParams = new HttpParams();
    httpParams = httpParams.set('subcategory', subcategory);

    return this.http
      .get<BackendResponse<Product[]>>(url, { params: httpParams })
      .pipe(
        map((arg: any) => deserializeResponse<Product[]>(arg, Product)),
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant('products.error.inProductsRetrieving')
          );
          return throwError(() => error);
        })
      );
  }

  updateResidentFavourites(
    customerId: number,
    residentId: number,
    favouriteProductsIds: number[],
    product?: Product
  ): Observable<BackendResponse<null>> {
    if (this.testCustomerService.isLoggedInAsTestCustomer()) {
      this.testCustomerService.updateResidentFavourites(
        residentId,
        favouriteProductsIds,
        product
      );
      return of({
        status: ResponseStatus.Success,
        message: 'Test customer favourite products updated to local storage',
      });
    }

    const url = replaceUrlParams(
      this.configService.endpoints.products.updateResidentFavourites,
      ['customerId', 'residentId'],
      [customerId, residentId]
    );
    return this.http
      .put<BackendResponse<null>>(url, { favouriteProductsIds })
      .pipe(
        catchError(error => {
          if (error.isHandled) return;
          this.snackbarService.error(
            this.translateService.instant(
              'products.error.inUpdatingFavouriteProducts'
            )
          );
          return throwError(() => error);
        })
      );
  }
}
